import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "Hooks";
import { Dropdown, ButtonDisclosure } from "@toddle-edu/ds-web";

import DualPanelManageMembers from "../DualPanelManageMembers";
import QueryWrapper, { metadataProps } from "./QueryWrapper";
import {
  groupByOptionsForCustomRecipient,
  ENTITY_TYPE,
  USER_TYPES,
} from "MyCalendar/components/EventModalV2/modules/Utils";
import useSchools from "MyCalendar/hooks/useSchools";
import useSchoolSelection from "AppComponents/SharedEntities/SelectParticipantsModal/hooks/useSchoolSelection";
import SchoolList from "AppComponents/SharedEntities/common/SchoolList";
import { isBigOrgFFEnabledAndStaffUser } from "MyCalendar/modules/Utils";
import classes from "./CustomSelectionList.scss";
import useReduxState from "Hooks/useReduxState";
import { getUserCurriculumPrograms } from "modules/CommonSelectors";

const SchoolFilterHeader = ({
  schools,
  selectedSchoolIds,
  onSchoolChange,
  isAllSchoolsSelected,
  selectedSchoolLabel,
}) => {
  const { t } = useI18n();
  return (
    <Dropdown
      dsVersion="2.0"
      overlay={
        <div className={classes.bgColor}>
          <SchoolList
            schools={schools}
            selectedSchoolIds={selectedSchoolIds}
            onSchoolChange={onSchoolChange}
            isAllSchoolsSelected={isAllSchoolsSelected}
          />
        </div>
      }
    >
      <ButtonDisclosure
        dsVersion="2.0"
        variant="neutral"
        type="outlined"
        size="medium"
      >
        <span className={classes.buttonText}>
          {t("common:school")}: {selectedSchoolLabel}
        </span>
      </ButtonDisclosure>
    </Dropdown>
  );
};

const CustomSelectionList = ({
  setSelectedRecipientList,
  sharedWith,
  selectedCustomList,
  leftCustomHeader,
  currentCurriculumProgramId,
}) => {
  const { schools } = useSchools();
  const userCurriculumPrograms = useReduxState(getUserCurriculumPrograms);
  const userCurriculumProgramIds = _.map(userCurriculumPrograms, "id");
  const [selectedSchoolIds, setSelectedSchoolIds] = useState(null);
  const {
    onSchoolChange,
    isAllSchoolsSelected,
    selectedSchoolLabel,
    allSchoolIds,
  } = useSchoolSelection(schools, selectedSchoolIds, setSelectedSchoolIds);

  const showSchoolFilter =
    isBigOrgFFEnabledAndStaffUser() && _.size(schools) > 1;

  // null = all schools → all enabled school Ids; convert Set to array at the API boundary
  let curriculumProgramIds = [];
  if (isBigOrgFFEnabledAndStaffUser()) {
    curriculumProgramIds =
      selectedSchoolIds === null ? allSchoolIds : [...selectedSchoolIds];
  } else {
    curriculumProgramIds = userCurriculumProgramIds;
  }

  // When exactly one school is selected, use its label for staff in the individual list
  const staffCurriculumProgramLabel =
    showSchoolFilter && _.size(selectedSchoolIds) === 1
      ? _.get(
          _.find(schools, s => selectedSchoolIds.has(s.id)),
          "label"
        )
      : undefined;

  const schoolFilterUI = showSchoolFilter ? (
    <SchoolFilterHeader
      schools={schools}
      selectedSchoolIds={selectedSchoolIds}
      onSchoolChange={onSchoolChange}
      isAllSchoolsSelected={isAllSchoolsSelected}
      selectedSchoolLabel={selectedSchoolLabel}
    />
  ) : (
    leftCustomHeader
  );

  useEffect(() => {
    const filteredCustomList = _.filter(
      selectedCustomList,
      selectedCustomListItem => {
        const userType = USER_TYPES[_.get(selectedCustomListItem, "type")];
        return _.includes(sharedWith, userType);
      }
    );
    setSelectedRecipientList(prevSelections => ({
      ...prevSelections,
      [ENTITY_TYPE.CUSTOM]: filteredCustomList,
    }));
  }, [sharedWith]);

  const { t } = useI18n();
  const handleRemoveUsers = users => {
    const userIds = _.map(users, "id");
    setSelectedRecipientList(prevSelections => {
      const filteredusers = _.filter(
        prevSelections[ENTITY_TYPE.CUSTOM] || [],
        user => !_.includes(userIds, user.id)
      );
      return {
        ...prevSelections,
        [ENTITY_TYPE.CUSTOM]: filteredusers,
      };
    });
  };

  const handleAddUsers = users => {
    setSelectedRecipientList(prevSelections => {
      const existingCustomUsers = prevSelections[ENTITY_TYPE.CUSTOM] || [];
      const allUsers = _.uniqBy([...existingCustomUsers, ...users], "id");
      return {
        ...prevSelections,
        [ENTITY_TYPE.CUSTOM]: allUsers,
      };
    });
  };

  const initUsersState = useMemo(() => {
    return _.reduce(
      selectedCustomList,
      (acc, user) => {
        acc[user.id] = user;
        return acc;
      },
      {}
    );
  }, [selectedCustomList]);

  return (
    <DualPanelManageMembers
      showTypeFilter
      handleAddUsers={handleAddUsers}
      handleRemoveUsers={handleRemoveUsers}
      showSchoolName={showSchoolFilter}
      staffCurriculumProgramLabel={staffCurriculumProgramLabel}
      renderQueryWrapper={props => (
        <QueryWrapper
          showCohortHeader={false}
          sharedWith={sharedWith}
          curriculumProgramIds={curriculumProgramIds}
          getOnlyUserId={isBigOrgFFEnabledAndStaffUser()}
          getCurriculumProgram={showSchoolFilter}
          {...props}
        />
      )}
      initUsersState={initUsersState}
      metadataProps={metadataProps}
      searchPlaceholder="announcement:search_members"
      typeFilterOptions={groupByOptionsForCustomRecipient({
        t,
        currentCurriculumProgramId,
      })}
      emptyTextQueryCond={() => true}
      showEmptyTextQuery={true}
      showClearAllBtn={true}
      autoFocusSearchInput={false}
      isChecklistFilter={false}
      isRevampEnabled={true}
      sharedWith={sharedWith}
      leftCustomHeader={schoolFilterUI}
      enableLazyHydration={isBigOrgFFEnabledAndStaffUser()}
    />
  );
};

export default CustomSelectionList;
